import { getIsAdmin } from '../../../../utils/getIsAdmin'

export const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function canManagePrograms(groups: unknown, nexusAdminGroups = '') {
  if (!Array.isArray(groups)) return false
  const configured = nexusAdminGroups.split(',').map(group => group.trim()).filter(Boolean)
  return getIsAdmin(groups) || groups.some(group => typeof group === 'string' && configured.includes(group))
}

export interface ProgramSummary {
  id: string
  name: string
  description: string
  make: string
  model: string
  hidden: boolean
  active: boolean
}

export type ProgramSort = 'name' | 'make' | 'model' | 'visibility'

export function filterPrograms<T extends ProgramSummary>(
  programs: T[],
  search: string,
  make: string,
  model: string,
  visibility: string,
  active: string,
  sort: ProgramSort,
  direction: 'asc' | 'desc'
) {
  const needle = search.trim().toLowerCase()
  return programs.filter(program =>
    (!needle || [program.name, program.description, program.make, program.model, program.id]
      .some(value => value.toLowerCase().includes(needle))) &&
    (!make || program.make === make) &&
    (!model || program.model === model) &&
    (!visibility || program.hidden === (visibility === 'hidden')) &&
    (!active || program.active === (active === 'active'))
  ).sort((a, b) => {
    const first = sort === 'visibility' ? (a.hidden ? 'Hidden' : 'Visible') : a[sort]
    const second = sort === 'visibility' ? (b.hidden ? 'Hidden' : 'Visible') : b[sort]
    const comparison = first.localeCompare(second) || a.id.localeCompare(b.id)
    return direction === 'asc' ? comparison : -comparison
  })
}

export interface ProgramPrice {
  id: string
  reservationFee?: number
  deposit?: number
  startFee?: number
  monthlyRent?: number
  priceTermMonths?: number
  fromDate?: number
  toDate?: number
  hidden?: boolean
  editable: boolean
}

export type PriceSort = keyof Pick<ProgramPrice,
  'monthlyRent' | 'startFee' | 'deposit' | 'reservationFee' | 'fromDate' | 'toDate' | 'hidden'>

export function sortPrices(prices: ProgramPrice[], sort: PriceSort, direction: 'asc' | 'desc') {
  return [...prices].sort((a, b) => {
    const first = a[sort]
    const second = b[sort]
    if (first === undefined || second === undefined) {
      if (first === undefined && second !== undefined) return 1
      if (second === undefined && first !== undefined) return -1
      return 0
    }
    const comparison = Number(first) - Number(second)
    return direction === 'asc' ? comparison : -comparison
  })
}

export const PRICE_LIMIT_MESSAGE = 'A program can have at most 3 active, visible prices at a time.'
export const INCOMPLETE_PRICES_MESSAGE = 'Cannot verify the price limit because pricing data is incomplete. Refresh or contact support.'

type PriceSchedule = Pick<ProgramPrice, 'id' | 'fromDate' | 'toDate' | 'hidden'>
type Interval = { start: number; end: number }

function visibleInterval(price: PriceSchedule, now: number): Interval | null | undefined {
  if (price.hidden === true) return null
  const validDate = (value?: number) =>
    Number.isSafeInteger(value) && value >= 0 && value <= 8640000000000000
  if (validDate(price.toDate) && price.toDate !== 0 && price.toDate <= now) return null
  if (price.hidden !== false || !validDate(price.fromDate) || !validDate(price.toDate)) return undefined
  // Zero dates are unbounded; starts are inclusive and ends are exclusive.
  const start = Math.max(price.fromDate || -Infinity, now)
  const end = price.toDate || Infinity
  return start < end ? { start, end } : null
}

export function getPriceScheduleStatus(prices: PriceSchedule[], now = Date.now()) {
  const intervals = prices.map(price => visibleInterval(price, now))
  const known = intervals.filter((interval): interval is Interval => !!interval)
  const starts = known.map(interval => interval.start)
  return {
    activeVisibleCount: known.filter(interval => interval.start <= now && now < interval.end).length,
    hasIncompletePrices: intervals.some(interval => interval === undefined),
    exceedsLimit: starts.some(start => known.filter(interval => interval.start <= start && start < interval.end).length > 3),
  }
}

export function getPriceLimitError(prices: PriceSchedule[], candidate: PriceSchedule, now = Date.now()): string | undefined {
  const original = candidate.id ? prices.find(price => price.id === candidate.id) : undefined
  const next = visibleInterval(candidate, now)
  if (next === null) return undefined
  if (!next) return INCOMPLETE_PRICES_MESSAGE
  const previous = original && visibleInterval(original, now)
  // Always permit unchanged schedules and corrective hiding/shortening, even for legacy violations.
  if (previous && next.start >= previous.start && next.end <= previous.end) return undefined
  if (original && previous === undefined) return INCOMPLETE_PRICES_MESSAGE
  const others = prices.filter(price => !candidate.id || price.id !== candidate.id)
    .map(price => visibleInterval(price, now))
  if (others.some(interval => interval === undefined)) return INCOMPLETE_PRICES_MESSAGE
  const intervals = others.filter((interval): interval is Interval => !!interval)
  const boundaries = [next.start, ...(previous ? [previous.end] : []), ...intervals.map(interval => interval.start)]
  return boundaries.some(time => {
    if (time < next.start || time >= next.end) return false
    const count = intervals.filter(interval => interval.start <= time && time < interval.end).length
    const before = count + (previous && previous.start <= time && time < previous.end ? 1 : 0)
    return count + 1 > 3 && count + 1 > before
  }) ? PRICE_LIMIT_MESSAGE : undefined
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? value as Record<string, unknown> : {}
}

function integer(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isSafeInteger(value) ? value : undefined
}

// Legacy pricing reads can omit fields needed for a full Price update.
// Such rows remain visible, but must not be written back with invented defaults.
export function getProgramPrices(pricingOptions: unknown): ProgramPrice[] {
  if (!Array.isArray(pricingOptions)) return []
  return pricingOptions.flatMap(value => {
    const group = record(value)
    if (!Array.isArray(group.pricingOptionsList)) return []
    return group.pricingOptionsList.map(value => {
      const option = record(value)
      const price: ProgramPrice = {
        id: typeof option.id === 'string' ? option.id : '',
        reservationFee: integer(option.reservationFee),
        deposit: integer(option.deposit),
        startFee: integer(option.startFee),
        monthlyRent: integer(option.monthlyRent),
        priceTermMonths: integer(option.priceTermMonths),
        fromDate: integer(option.fromDate ?? group.fromDate),
        toDate: integer(option.toDate ?? group.toDate),
        hidden: typeof option.hidden === 'boolean' ? option.hidden : undefined,
        editable: false,
      }
      const amounts = [price.reservationFee, price.deposit, price.startFee, price.monthlyRent]
      price.editable = UUID_PATTERN.test(price.id) &&
        amounts.every(amount => amount !== undefined && amount >= 0 && amount <= 2147483647) &&
        price.monthlyRent > 0 &&
        price.priceTermMonths >= 0 && price.priceTermMonths <= 2147483647 &&
        price.fromDate !== undefined && price.fromDate >= 0 && price.fromDate <= 8640000000000000 &&
        price.toDate !== undefined && price.toDate >= 0 && price.toDate <= 8640000000000000 &&
        (!price.toDate || !price.fromDate || price.toDate > price.fromDate) &&
        price.hidden !== undefined
      return price
    })
  })
}
